import styled from '@emotion/styled';
import { DBTimestampType } from '@racemap/utilities/api-client';
import { MaxMobileWindowWidth } from '@racemap/utilities/consts/common';
import { Button, Space, Typography } from 'antd';
import { DateTime } from 'luxon';
import { useStore } from '../../store/reducers';
import { TimestampEditor } from '../BasicComponents';
import { HelperModal } from '../Modal';
import TimeFramesIndicator, { type TimeFrames } from './TimeFramesIndicator';

const { Text } = Typography;

export const FetchReadsOptions = ({
  timeFrames,
  showFilterControls = true,
}: {
  timeFrames: TimeFrames;
  showFilterControls?: boolean;
}): React.ReactElement => {
  const { setReadsFilter } = useStore((s) => s.prediction.actions);
  const readsFilter = useStore((s) => s.prediction.readsFilter);

  return (
    <FetchReadsOptionsContainer>
      <TimeFramesIndicator timeFrames={timeFrames} />
      <TimeStampEditorsContainer>
        <Space orientation="vertical" size={6}>
          {showFilterControls && (
            <FilterControlsRow>
              <CompactButtonsContainer>
                <Button
                  size="small"
                  type={
                    readsFilter.timestampFilterType === DBTimestampType.ReceivedAt
                      ? 'primary'
                      : 'default'
                  }
                  onClick={() =>
                    setReadsFilter({
                      timestampFilterType: DBTimestampType.ReceivedAt,
                      offset: 0,
                    })
                  }
                >
                  Received At
                </Button>
                <Button
                  size="small"
                  type={
                    readsFilter.timestampFilterType === DBTimestampType.ReadAt
                      ? 'primary'
                      : 'default'
                  }
                  onClick={() =>
                    setReadsFilter({
                      timestampFilterType: DBTimestampType.ReadAt,
                      offset: 0,
                    })
                  }
                >
                  Read At
                </Button>
                <Button
                  id="prediction_show_test_data"
                  size="small"
                  type={readsFilter.isTest ? 'primary' : 'default'}
                  onClick={() => setReadsFilter({ isTest: !readsFilter.isTest, offset: 0 })}
                >
                  Tests only
                </Button>
              </CompactButtonsContainer>
              <HelperModal title="Read Filters" size="lg">
                <Space orientation="vertical">
                  <Text type="secondary">
                    Begin/Finish applies to <strong>{readsFilter.timestampFilterType}</strong>.
                    Sorting is independent, so it is valid to filter by "Received At" while sorting
                    by "Read At" (or the other way around).
                  </Text>
                  <Text type="secondary">
                    Example: If you filter by "Received At" from 1st Jan, reads received after 1st
                    Jan are included, even when their "Read At" timestamp is before 1st Jan.
                  </Text>
                  <Text type="secondary">
                    "Test Data only" shows only reads marked as test in the database and hides
                    production reads.
                  </Text>
                </Space>
              </HelperModal>
            </FilterControlsRow>
          )}
          <TimestampEditor
            minDate={DateTime.now().minus({ days: 500 })}
            timestamp={readsFilter.startTime.toISOString()}
            label="Begin"
            setTimestamp={(start) => setReadsFilter({ startTime: new Date(start) })}
          />
          <TimestampEditor
            minDate={DateTime.now().minus({ days: 499 })}
            timestamp={readsFilter.endTime.toISOString()}
            label="Finish"
            setTimestamp={(end) => setReadsFilter({ endTime: new Date(end) })}
          />
        </Space>
      </TimeStampEditorsContainer>
    </FetchReadsOptionsContainer>
  );
};

const FetchReadsOptionsContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  overflow: hidden;
  margin-bottom: 7px;

  .input-group {
    width: 400px;

    @media (max-width: ${MaxMobileWindowWidth}px) {
      width: 100%;
    }
  }
`;

const TimeStampEditorsContainer = styled.div`
  margin-right: 10px;
  margin-top: 0;

  .ant-space-item {
    width: 100%;
  }

  .ant-space-item > div > div:first-of-type {
    min-width: 48px;
  }

  .custom-datepicker {
    flex: 1;
    min-width: 0;
    width: auto !important;
  }
`;

const FilterControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: nowrap;
  margin-left: -5px;
`;

const CompactButtonsContainer = styled.div`
  display: inline-flex;

  .ant-btn {
    border-radius: 0;
  }

  .ant-btn:first-of-type {
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
  }

  .ant-btn:last-of-type {
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }

  .ant-btn + .ant-btn {
    margin-left: -1px;
  }
`;
